/**
 * Pure Up next assembly (Tonight, and the Library rail).
 * "In progress" = a series the user is `watching`, most recently updated first
 * (the `/v1/home` order), joined with `/v1/calendar/mine` to flag a NEW
 * episode: the calendar has an airing for that show in the last 7 days
 * (≤ now) that sits beyond the user's watched count (the calendar's
 * `library[tmdb_id].watchedCount` — a total across seasons; docs/api-schema.md
 * `GET /v1/calendar/mine`).
 */

import { airingIsNew, type EpisodeRef } from '@shared/resume';
import type { LibraryItem, MyCalendar, Season, Title } from '@shared/types';

import { airDay } from '@/features/calendar/dates';

export const NEW_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

export interface Airing {
  season: number;
  episode: number;
  airDate: string;
}

export interface UpNextItem {
  title: Title;
  watchedCount: number;
  totalEpisodes: number;
  /** Calendar airings for this show within the last 7 days, oldest first. */
  recentAirings: Airing[];
  /**
   * Coarse NEW flag from list data alone (a recent airing exists).
   * `airingBeyondWatched` refines it once the season summaries are known —
   * the card only shows the tag after that refinement.
   */
  isNew: boolean;
}

/** Air dates are date-only strings read as LOCAL days (calendar/dates.ts). */
function ts(iso: string): number {
  return airDay(iso).getTime();
}

/**
 * True when any recent airing is one the user has not reached: at or after
 * the resume point (`@shared/resume`), or past the catalog when caught up.
 * Watched-count arithmetic would flag every airing of a series whose early
 * seasons were skipped.
 */
export function airingBeyondWatched(
  airings: Airing[],
  seasons: Season[],
  resume: EpisodeRef | null,
): boolean {
  return airings.some((a) => airingIsNew(a, resume, seasons));
}

export function buildUpNext(
  items: LibraryItem[],
  calendar: MyCalendar | undefined,
  now: number,
  limit: number,
): UpNextItem[] {
  const out: UpNextItem[] = [];
  for (const item of items) {
    if (out.length >= limit) break;
    if (item.status !== 'watching' || item.title.type !== 'show') continue;
    const id = item.title.id;
    const recentAirings: Airing[] = (calendar?.entries ?? [])
      .filter((e) => e.titleId === id)
      .filter((e) => {
        const t = ts(e.airDate);
        return !Number.isNaN(t) && t <= now && now - t <= NEW_WINDOW_MS;
      })
      .map((e) => ({ season: e.seasonNumber, episode: e.episodeNumber, airDate: e.airDate }))
      .sort((a, b) => ts(a.airDate) - ts(b.airDate));
    const watchedCount = calendar?.library[id]?.watchedCount ?? item.watchedCount;
    const total = item.totalEpisodes;
    const caughtUp = total > 0 && watchedCount >= total;
    // A caught-up series has nothing to continue — unless a new episode aired
    // after the catalog counted its total (the calendar knows first).
    if (caughtUp && recentAirings.length === 0) continue;
    out.push({
      title: item.title,
      watchedCount,
      totalEpisodes: total,
      recentAirings,
      isNew: recentAirings.length > 0,
    });
  }
  return out;
}
